import React, { useState, useEffect, useCallback, useRef } from 'react';
import { devLog, devError } from '../../utils/devLogger';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import { getTerms, preBid, getPreBidDetail, agreeAuction, getAgreeAuction } from '../../lib/api/user/userApi';
import { PreBidDetailData } from '../../lib/api/user/userApi.types';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { FormTitle } from '../../customComponents/FormTitle';
import { CustomDropdown } from '../../customComponents/CustomDropdown';
import AgreementButton from '../../components/AgreementButton';
import TermsConsentSheet from '../../components/TermsConsentSheet';
import AnimatedBorderButton from '../../components/AnimatedBorderButton';
import BottomFixedAction from '../../components/BottomFixedAction';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px; /* 고정 버튼 영역을 위한 하단 패딩 */
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
  gap: 24px;
`;

// 정보 섹션
const InfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 32px 0;
  margin-bottom: 8px;
  border-top: 1px solid #f3f3f3;
  border-bottom: 1px solid #f3f3f3;
`;

const LotImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  background: #f0f0f0;
`;

const LotInfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const LotName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  line-height: 1.4;
  text-align: left;
  align-self: flex-start;
`;

const LotId = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  text-align: left;
  align-self: flex-start;
`;

// 금액 섹션
const AmountSection = styled.div`
  background: white;
  border-radius: 12px;
  padding-top: 20px;
  margin-bottom: 8px;
`;

const DropdownContainer = styled.div`
  margin-bottom: 20px;
`;

const FeeCalculationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #262626;
`;

const FeeDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e9ecef;
  margin: 12px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #7a6647;
  padding-top: 8px;
`;

// 툴팁 관련 스타일
const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const QuestionIcon = styled.div.attrs<{}, { ref?: React.Ref<HTMLDivElement> }>(() => ({}))`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #666666;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #333333;
  }
`;

const Tooltip = styled.div<{ 
  visible: boolean; 
  $left: string; 
  $transform: string; 
  $arrowLeft: string; 
}>`
  position: absolute;
  bottom: 100%;
  left: ${props => props.$left};
  transform: ${props => props.$transform};
  background-color: #333333;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s, visibility 0.2s;
  margin-bottom: 4px;
  min-width: 280px; /* 최소 너비 설정 */
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: ${props => props.$arrowLeft};
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #333333;
  }
`;

// 드롭다운 옵션 스타일 컴포넌트들
const DropdownOptionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const CurrencyText = styled.span`
  color: #b6a488;
  font-size: 12px;
  font-weight: 500;
`;

const AmountText = styled.span`
  color: #7a6647;
  font-size: 16px;
  font-weight: 600;
`;

// 입찰 유의사항 스타일
const BiddingNoticeContainer = styled.div`
  background: #f8f5f0;
  border-radius: 8px;
  padding: 16px;
  min-height: 137px; /* 최소 높이로 변경 */
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const BiddingNoticeTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0;
`;

const InfoIcon = styled.svg`
  width: 20px;
  height: 20px;
  fill: #666666;
  flex-shrink: 0;
`;

const BiddingNoticeList = styled.ul`
  margin: 0;
  padding: 0 0 0 12px; /* 왼쪽에 12px 여백 추가 */
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BiddingNoticeItem = styled.li`
  font-size: 14px;
  color: #666666;
  line-height: 1.4;
  position: relative;
  padding-left: 16px; /* 점과 텍스트 사이 간격 줄임 */
  text-align: left; /* 명시적으로 왼쪽 정렬 */
  
  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #999999;
  }
`;

// 동의 섹션 스타일
const AgreementSectionWrapper = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 8px;
`;

const AgreementItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ViewButton = styled.button`
  background: none;
  border: none;
  color: #666666;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #333333;
  }
`;

// 기존 FixedButtonContainer/ButtonWrapper는 공통 BottomFixedAction으로 대체

const bidRequestTexts = {
  ko: {
    title: 'LOT 입찰 신청',
    infoSection: '경매 정보',
    amountSection: '입찰가',
    bidAmountLabel: '입찰가',
    bidAmountPlaceholder: '입찰가를 선택해주세요',
    auctionHouseFee: '경매장 수수료',
    onlineAuctionFee: '온라인경매 수수료',
    feeVAT: '경매장 수수료 부가세',
    onlineFeeVAT: '온라인경매 수수료 부가세',
    feeVATTooltip: '경매장 수수료의 10%에 대한 부가세입니다',
    onlineFeeVATTooltip: '온라인경매 수수료의 10%에 대한 부가세입니다',
    totalAmount: '총 합계',
    currency: 'KRW',
    agreementSection: '동의사항',
    allAgreeTitle: '전체 동의',
    auctionTermsTitle: '경매장 이용약관 동의',
    auctionTermsDetailTitle: '경매장 이용약관 상세',
    agreeButton: '동의',
    agreeSuccessMessage: '사전입찰과 라이브입찰에서 본 경매의 모든 Lot에 대해서 동의 처리 되었습니다',
    bidButton: '꾹 눌러 입찰 신청',
    bidButtonTooltip: '1초간 꾹 눌러서 입찰',
    biddingNoticeTitle: '입찰 시 유의사항',
    biddingNoticeItem1: '라이브 경매 중에는 금액 수정이 불가합니다.',
    biddingNoticeItem2: '직접·메일 입찰 금액 동일 시, 시간 순 우선 처리됩니다.',
    biddingNoticeItem3: '경매 수수료는 경매 별로 상이합니다.'
  },
  en: {
    title: 'LOT Bid Request',
    infoSection: 'Auction Information',
    amountSection: 'Bid Price',
    bidAmountLabel: 'Bid Price',
    bidAmountPlaceholder: 'Please select bid price',
    auctionHouseFee: 'Auction House Fee',
    onlineAuctionFee: 'Online Auction Fee',
    feeVAT: 'Fee VAT',
    onlineFeeVAT: 'Online Fee VAT',
    feeVATTooltip: 'VAT on the auction house fee (10%)',
    onlineFeeVATTooltip: 'VAT on the online auction fee (10%)',
    totalAmount: 'Total Amount',
    currency: 'USD',
    agreementSection: 'Agreements',
    allAgreeTitle: 'Agree All',
    auctionTermsTitle: 'Agree to Auction Terms',
    auctionTermsDetailTitle: 'Auction Terms Details',
    agreeButton: 'Agree',
    agreeSuccessMessage: 'Agreement has been applied to all lots in this auction for both pre-bid and live bidding.',
    bidButton: 'Hold to Submit Bid',
    bidButtonTooltip: 'Hold for 1 second to bid',
    biddingNoticeTitle: 'Bidding Precautions',
    biddingNoticeItem1: 'Amount modification is not possible during live auction.',
    biddingNoticeItem2: 'When direct and mail bid amounts are the same, they are processed in time order.',
    biddingNoticeItem3: 'Auction fees vary by auction.'
  }
};

function BidRequest() {
  const navigate = useNavigate();
  const { lotId } = useParams<{ lotId: string }>();
  const { lang } = useLang();
  const { user } = useUserAuth();
  const texts = bidRequestTexts[lang];
  const isDevMode = process.env.NODE_ENV === 'development';
  
  // 상태 관리 - lotData를 로컬 상태로 변경
  const [lotData, setLotData] = useState<PreBidDetailData | null>(null);
  
  // 상태 관리
  const [auctioneerTerms, setAuctioneerTerms] = useState<string>('');
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);

  // 기본 auctioneer 약관 (API에서 가져올 수 없을 때만 사용)
  const getDefaultAuctioneerTerms = useCallback(() => {
    const auctioneerName = lotData?.auctioneer?.name || '경매장';
    
    return `
      <h3>${auctioneerName} 이용약관</h3>
      <p>약관 정보를 불러올 수 없습니다. 잠시 후 다시 시도해주세요.</p>
    `;
  }, [lotData]);
  
  // lotId가 있으면 API에서 데이터 가져오기
  useEffect(() => {
    const fetchLotData = async () => {
      if (!lotId) {
        const errorMessage = lang === 'ko' ? 'LOT 정보를 찾을 수 없습니다.' : 'LOT information not found.';
        toast.error(errorMessage);
        navigate(-1);
        return;
      }

      try {
        devLog('🔍 [BidRequest] API에서 데이터 가져오기:', lotId);
        const response = await getPreBidDetail(lotId);
        devLog('🔍 [BidRequest] API 응답:', response.data);
        
        if (response.data.statusCode === 200 && response.data.data) {
          const apiLotData: any = response.data.data; // LOT 데이터가 직접 반환됨
          
          // API 응답을 PreBidDetailData 형식에 맞게 변환
          const transformedLotData: PreBidDetailData = {
            index: apiLotData.index,
            code: apiLotData.code || `LOT.${apiLotData.index}`, // API에서 code 필드 사용, 없으면 fallback
            name: apiLotData.name, // API에서 name 필드로 직접 반환
            description: apiLotData.description || '',
            images: apiLotData.images || [],
            startPrice: apiLotData.startPrice || 0,
            exchangeRate: apiLotData.exchangeRate || 1400,
            startPriceUsd: apiLotData.startPriceUsd || 0,
            recommendNo: apiLotData.recommendNo || null,
            likedUserIndexes: apiLotData.likedUserIndexes || [],
            likeCount: apiLotData.likeCount || 0,
            createBy: apiLotData.createBy || '',
            updateBy: apiLotData.updateBy || '',
            createAt: apiLotData.createAt || '',
            updateAt: apiLotData.updateAt || '',
            category: apiLotData.category || { code: '', name: '', krName: '' },
            subCategory: apiLotData.subCategory || { code: '', name: '', krName: '' },
            auctioneer: {
              index: apiLotData.auctioneer?.index || 0,
              name: apiLotData.auctioneer?.name || '',
              description: apiLotData.auctioneer?.description || '',
              profilePic: apiLotData.auctioneer?.profilePic || '',
              term: apiLotData.auctioneer?.term
            },
            auction: {
              index: apiLotData.auction?.index || 0,
              name: apiLotData.auction?.name || '',
              description: apiLotData.auction?.description || '',
              url: apiLotData.auction?.url || '',
              auctionLotNo: apiLotData.auction?.auctionLotNo || 0,
              preBidStartAt: apiLotData.auction?.preBidStartAt || '',
              preBidEndAt: apiLotData.auction?.preBidEndAt || '',
              auctionStartAt: apiLotData.auction?.auctionStartAt || '',
              auctionEndAt: apiLotData.auction?.auctionEndAt || '',
              paymentDueAt: apiLotData.auction?.paymentDueAt || '',
              auctionFeeRate: apiLotData.auction?.auctionFeeRate || 0,
              platformFeeRate: apiLotData.auction?.platformFeeRate || 0,
              createAt: apiLotData.auction?.createAt || '',
              updateAt: apiLotData.auction?.updateAt || null,
              biddingRanges: apiLotData.auction?.biddingRanges || []
            },
            preBids: apiLotData.preBids || []
          };
          
          setLotData(transformedLotData);
          devLog('🔍 [BidRequest] API에서 데이터 로드 완료:', transformedLotData);
        } else {
          devError('🔍 [BidRequest] API 응답 실패:', response.data);
          const errorMessage = lang === 'ko' 
            ? `LOT 정보를 불러올 수 없습니다. (상태코드: ${response.data.statusCode})`
            : `Unable to load LOT information. (Status code: ${response.data.statusCode})`;
          toast.error(errorMessage);
          navigate(-1);
        }
      } catch (error) {
        devError('🔍 [BidRequest] LOT 정보 조회 실패:', error);
        const errorMessage = error instanceof Error ? error.message : (lang === 'ko' ? '알 수 없는 오류' : 'Unknown error');
        const fullErrorMessage = lang === 'ko' 
          ? `LOT 정보를 불러오는 중 오류가 발생했습니다: ${errorMessage}`
          : `An error occurred while loading LOT information: ${errorMessage}`;
        toast.error(fullErrorMessage);
        navigate(-1);
      }
    };

    fetchLotData();
  }, [lotId, navigate, lang]);

  // auctioneer 약관 로드
  useEffect(() => {
    if (!lotData) {
      return; // lotData가 없으면 아무것도 하지 않음
    }

    // auctioneer의 term 데이터가 있으면 바로 사용, 없으면 API에서 조회
    if (lotData.auctioneer?.term) {
      setAuctioneerTerms(lotData.auctioneer.term);
      setIsLoadingTerms(false);
    } else {
      // auctioneer별 약관 조회 (fallback)
      const fetchAuctioneerTerms = async () => {
        try {
          setIsLoadingTerms(true);
          const response = await getTerms({ termLang: lang === 'ko' ? 'KOR' : 'ENG' });
          
          if (response.data.statusCode === 200 && response.data.data?.terms) {
            // auctioneer에 특화된 약관을 찾거나 기본 약관 사용
            const auctionTerms = response.data.data.terms.find(
              term => term.termType === 'AUCTION' || term.termType === 'AUCTIONEER'
            );
            
            if (auctionTerms) {
              setAuctioneerTerms(auctionTerms.termContent || getDefaultAuctioneerTerms());
            } else {
              setAuctioneerTerms(getDefaultAuctioneerTerms());
            }
          } else {
            setAuctioneerTerms(getDefaultAuctioneerTerms());
          }
        } catch (err) {
          devError('약관 조회 실패:', err);
          setAuctioneerTerms(getDefaultAuctioneerTerms());
        } finally {
          setIsLoadingTerms(false);
        }
      };

      fetchAuctioneerTerms();
    }
  }, [lotData, navigate, lang, getDefaultAuctioneerTerms]);

  useEffect(() => {
    if (!isDevMode) {
      return;
    }

    const auctionIndex = lotData?.auction?.index;

    if (!auctionIndex) {
      setIsIndividualAgreed(false);
      setIsAgreementCompleted(false);
      return;
    }

    const fetchAgreementStatus = async () => {
      try {
        const response = await getAgreeAuction(auctionIndex);

        if (response.data.statusCode !== 200) {
          setIsIndividualAgreed(false);
          setIsAgreementCompleted(false);
          return;
        }

        const isAgreed = response.data.data?.agreed === true;

        setIsIndividualAgreed(isAgreed);
        setIsAgreementCompleted(isAgreed);
      } catch (error) {
        devError('경매 동의 여부 조회 실패:', error);
        setIsIndividualAgreed(false);
        setIsAgreementCompleted(false);
      }
    };

    fetchAgreementStatus();
  }, [isDevMode, lotData?.auction?.index]);
  
  // 통화 라벨 함수
  const getCurrencyLabel = useCallback(() => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    return isDomestic ? 'KRW' : 'USD';
  }, [user?.locationType]);
  
  // 동적 입찰가 옵션 생성 함수
  const generateBidPriceOptions = useCallback(() => {
    if (!lotData?.auction?.biddingRanges || !lotData?.exchangeRate) {
      return [];
    }

    const safeNumber = (value: any, defaultValue: number = 0): number => {
      const num = Number(value);
      return Number.isFinite(num) ? num : defaultValue;
    };

    const startPriceKrw = safeNumber(lotData.startPrice, 0);
    const hasPreBids = Array.isArray(lotData.preBids) && lotData.preBids.length > 0;

    // preBids가 있으면 최고 preBid 기준, 없으면 startPrice 기준
    let maxBidPrice = hasPreBids
      ? lotData.preBids.reduce((max, bid) => Math.max(max, safeNumber(bid.price, 0)), 0)
      : startPriceKrw;

    if (!Number.isFinite(maxBidPrice) || maxBidPrice <= 0) {
      maxBidPrice = 100000;
    }

    // 해당 금액이 속하는 구간 찾기
    const biddingRanges = lotData.auction.biddingRanges;
    let currentRange = biddingRanges.find(range => {
      const minAmount = safeNumber(range.minBidAmount, 0);
      const maxAmount = range.maxBidAmount === null ? null : safeNumber(range.maxBidAmount, 0);
      
      return maxBidPrice >= minAmount && (maxAmount === null || maxBidPrice < maxAmount);
    });

    // 구간을 찾지 못했거나 maxBidPrice가 구간의 최대값과 같은 경우, 다음 구간을 찾기
    if (!currentRange) {
      // maxBidPrice가 어떤 구간의 maxAmount와 정확히 일치하는 경우 다음 구간 찾기
      const rangeIndex = biddingRanges.findIndex(range => 
        range.maxBidAmount !== null && maxBidPrice === safeNumber(range.maxBidAmount, 0)
      );
      
      if (rangeIndex !== -1 && rangeIndex + 1 < biddingRanges.length) {
        // 다음 구간이 존재하면 사용
        currentRange = biddingRanges[rangeIndex + 1];
      } else {
        // 마지막 구간이거나 구간을 찾을 수 없는 경우
        currentRange = biddingRanges[biddingRanges.length - 1];
      }
    }

    if (!currentRange) {
      // 방어적 코딩: 가장 마지막 구간(상한이 없는 구간)을 사용
      currentRange = biddingRanges[biddingRanges.length - 1];
      
      if (!currentRange) {
        // 구간이 없는 경우에도 10개 생성
        const fallbackRate = 1000;
        return Array.from({ length: 10 }, (_, i) => {
          const priceKrw = maxBidPrice + (fallbackRate * i);
          const priceUsd = Math.round((priceKrw / lotData.exchangeRate) * 100) / 100;

          return {
            value: priceKrw.toString(),
            content: (
              <DropdownOptionContainer>
                <CurrencyText>{getCurrencyLabel()}</CurrencyText>
                <AmountText>{user?.locationType === 'DOMESTIC' ? priceKrw.toLocaleString() : `${priceUsd.toFixed(2)}`}</AmountText>
              </DropdownOptionContainer>
            )
          };
        });
      }
    }

    // preBid가 전혀 없으면 시작가부터, 있으면 최고가 + 증액단위부터
    const rate = safeNumber(currentRange.rate, 1000);
    const startAmount = hasPreBids ? maxBidPrice + rate : maxBidPrice;
    const exchangeRate = lotData.exchangeRate;

    // 최대 10개까지 옵션 생성
    const options = [];
    for (let i = 0; i < 10; i++) {
      const priceKrw = startAmount + (rate * i);
      const priceUsd = Math.round((priceKrw / exchangeRate) * 100) / 100; // 소수점 2자리까지
      
      options.push({
        value: priceKrw.toString(), // 값은 항상 KRW 기준으로 저장
        content: (
          <DropdownOptionContainer>
            <CurrencyText>{getCurrencyLabel()}</CurrencyText>
            <AmountText>
              {user?.locationType === 'DOMESTIC' 
                ? priceKrw.toLocaleString() 
                : `${priceUsd.toFixed(2)}`
              }
            </AmountText>
          </DropdownOptionContainer>
        )
      });
    }

    return options;
  }, [lotData, user?.locationType, getCurrencyLabel]);

  // 입찰가 옵션들
  const bidPriceOptions = generateBidPriceOptions();

  const [selectedBidPrice, setSelectedBidPrice] = useState(''); // 기본값을 빈 문자열로 초기화
  const [isIndividualAgreed, setIsIndividualAgreed] = useState(false);
  const [isAgreementCompleted, setIsAgreementCompleted] = useState(false);
  const [isAgreeLoading, setIsAgreeLoading] = useState(false);
  const [showTermsSheet, setShowTermsSheet] = useState(false);
  const [viewedSection, setViewedSection] = useState('auction-terms');
  const [showVATTooltip, setShowVATTooltip] = useState(false);
  const [showOnlineVATTooltip, setShowOnlineVATTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: '50%', transform: 'translateX(-50%)', arrowLeft: '50%' });
  const [onlineTooltipPosition, setOnlineTooltipPosition] = useState({ left: '50%', transform: 'translateX(-50%)', arrowLeft: '50%' });
  const questionIconRef = useRef<HTMLDivElement>(null);
  const onlineQuestionIconRef = useRef<HTMLDivElement>(null);
  const shouldHideByAgreement = isDevMode && isAgreementCompleted;

  // 툴팁 위치 계산 함수
  const calculateTooltipPosition = useCallback(() => {
    if (!questionIconRef.current) return;
    
    const iconRect = questionIconRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const tooltipWidth = 300; // 예상 툴팁 너비
    
    // 아이콘이 화면 왼쪽에서 가까운 경우
    if (iconRect.left < tooltipWidth / 2) {
      setTooltipPosition({
        left: '0px',
        transform: 'none',
        arrowLeft: `${iconRect.width / 2}px`
      });
    }
    // 아이콘이 화면 오른쪽에서 가까운 경우
    else if (iconRect.right > windowWidth - tooltipWidth / 2) {
      setTooltipPosition({
        left: 'auto',
        transform: 'translateX(-100%)',
        arrowLeft: `${tooltipWidth - iconRect.width / 2}px`
      });
    }
    // 일반적인 경우 (중앙 정렬)
    else {
      setTooltipPosition({
        left: '50%',
        transform: 'translateX(-50%)',
        arrowLeft: '50%'
      });
    }
  }, []);

  const calculateOnlineTooltipPosition = useCallback(() => {
    if (!onlineQuestionIconRef.current) return;

    const iconRect = onlineQuestionIconRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const tooltipWidth = 300; // 예상 툴팁 너비

    if (iconRect.left < tooltipWidth / 2) {
      setOnlineTooltipPosition({
        left: '0px',
        transform: 'none',
        arrowLeft: `${iconRect.width / 2}px`
      });
    } else if (iconRect.right > windowWidth - tooltipWidth / 2) {
      setOnlineTooltipPosition({
        left: 'auto',
        transform: 'translateX(-100%)',
        arrowLeft: `${tooltipWidth - iconRect.width / 2}px`
      });
    } else {
      setOnlineTooltipPosition({
        left: '50%',
        transform: 'translateX(-50%)',
        arrowLeft: '50%'
      });
    }
  }, []);

  // 입찰가 옵션이 생성되면 첫 번째 옵션을 기본값으로 설정
  useEffect(() => {
    if (bidPriceOptions.length > 0) {
      if (!selectedBidPrice || selectedBidPrice === '') {
        setSelectedBidPrice(bidPriceOptions[0].value);
      } else if (!bidPriceOptions.find(option => option.value === selectedBidPrice)) {
        setSelectedBidPrice(bidPriceOptions[0].value);
      }
    }
  }, [bidPriceOptions, selectedBidPrice]);

  // 툴팁 외부 클릭 시 닫기 및 위치 계산
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showVATTooltip) {
        setShowVATTooltip(false);
      }
      if (showOnlineVATTooltip) {
        setShowOnlineVATTooltip(false);
      }
    };

    // 툴팁이 표시될 때 위치 계산
    if (showVATTooltip) {
      calculateTooltipPosition();
      // 화면 크기 변경 시에도 위치 재계산
      window.addEventListener('resize', calculateTooltipPosition);
    }

    if (showOnlineVATTooltip) {
      calculateOnlineTooltipPosition();
      window.addEventListener('resize', calculateOnlineTooltipPosition);
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', calculateTooltipPosition);
      window.removeEventListener('resize', calculateOnlineTooltipPosition);
    };
  }, [showVATTooltip, showOnlineVATTooltip, calculateTooltipPosition, calculateOnlineTooltipPosition]);

  // 디버깅 로그 추가
  devLog('🔍 [BidRequest] lotData 상태:', {
    lotData,
    hasLotData: !!lotData,
    lotDataKeys: lotData ? Object.keys(lotData) : null
  });

  // API에서 받았다고 가정하는 수수료율 - 실제 API 데이터 사용
  const feeRates = {
    auctionHouseFeeRate: lotData?.auction?.auctionFeeRate ?? 0, // API에서 가져온 수수료율
    onlineAuctionFeeRate: lotData?.auction?.platformFeeRate ?? 0, // API에서 가져온 플랫폼 수수료율
    vatRate: 10 // 10% (고정)
  };

  // 수수료 계산 함수 (사용자 locationType에 따른 처리)
  const calculateFees = (bidPrice: number) => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    const exchangeRate = lotData?.exchangeRate || 1409.09;
    
    // KRW 기준 계산
    const auctionHouseFeeKRW = Math.floor(bidPrice * (feeRates.auctionHouseFeeRate / 100));
    const onlineAuctionFeeKRW = Math.floor(bidPrice * (feeRates.onlineAuctionFeeRate / 100));
    const totalFeesKRW = auctionHouseFeeKRW + onlineAuctionFeeKRW;
    const feeVATKRW = isDomestic ? Math.floor(auctionHouseFeeKRW * (feeRates.vatRate / 100)) : 0;
    const onlineFeeVATKRW = isDomestic ? Math.floor(onlineAuctionFeeKRW * (feeRates.vatRate / 100)) : 0;
    const totalAmountKRW = bidPrice + totalFeesKRW + feeVATKRW + onlineFeeVATKRW;

    // USD 기준 계산 (해외 사용자용)
    const bidPriceUSD = bidPrice / exchangeRate;
    const auctionHouseFeeUSD = Math.round(bidPriceUSD * (feeRates.auctionHouseFeeRate / 100) * 100) / 100;
    const onlineAuctionFeeUSD = Math.round(bidPriceUSD * (feeRates.onlineAuctionFeeRate / 100) * 100) / 100;
    const totalFeesUSD = auctionHouseFeeUSD + onlineAuctionFeeUSD;
    const feeVATUSD = isDomestic ? Math.round(auctionHouseFeeUSD * (feeRates.vatRate / 100) * 100) / 100 : 0;
    const onlineFeeVATUSD = isDomestic ? Math.round(onlineAuctionFeeUSD * (feeRates.vatRate / 100) * 100) / 100 : 0;
    const totalAmountUSD = Math.round((bidPriceUSD + totalFeesUSD + feeVATUSD + onlineFeeVATUSD) * 100) / 100;

    return {
      auctionHouseFee: isDomestic ? auctionHouseFeeKRW : auctionHouseFeeUSD,
      onlineAuctionFee: isDomestic ? onlineAuctionFeeKRW : onlineAuctionFeeUSD,
      feeVAT: isDomestic ? feeVATKRW : feeVATUSD,
      onlineFeeVAT: isDomestic ? onlineFeeVATKRW : onlineFeeVATUSD,
      totalAmount: isDomestic ? totalAmountKRW : totalAmountUSD,
      isDomestic
    };
  };

  const formatNumber = (value: number) => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    if (isDomestic) {
      return value.toLocaleString();
    } else {
      // 해외 사용자는 USD로 표시
      return value.toFixed(2);
    }
  };



  // 이미지 URL 처리 함수
  const getImageSrc = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return "/common/no_image.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (imageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
  };

  // 약관 섹션 데이터 - auctioneerTerms 사용
  const termsSection = {
    key: 'auction-terms',
    title: texts.auctionTermsDetailTitle,
    content: auctioneerTerms || getDefaultAuctioneerTerms()
  };

  const handleViewTerms = () => {
    setShowTermsSheet(true);
  };

  const handleCloseTermsSheet = () => {
    setShowTermsSheet(false);
  };

  const handleSectionClick = (sectionKey: string) => {
    setViewedSection(sectionKey);
  };

  const isFormValid = () => {
    return selectedBidPrice && isIndividualAgreed;
  };

  const handleAgreeAuction = async () => {
    if (isAgreementCompleted || isAgreeLoading) {
      return;
    }

    // 운영 환경에서는 동의 API를 호출하지 않고 로컬 상태만 처리
    if (!isDevMode) {
      setIsIndividualAgreed(true);
      setShowTermsSheet(false);
      return;
    }

    const auctionIndex = lotData?.auction?.index;
    if (!auctionIndex) {
      const errorMessage = lang === 'ko' ? '경매 정보를 찾을 수 없습니다.' : 'Auction information not found.';
      toast.error(errorMessage);
      return;
    }

    try {
      setIsAgreeLoading(true);
      const response = await agreeAuction(auctionIndex);

      if (response.data.statusCode === 200 || response.data.statusCode === 201) {
        setIsIndividualAgreed(true);
        if (isDevMode) {
          setIsAgreementCompleted(true);
        }
        setShowTermsSheet(false);
        toast.success(texts.agreeSuccessMessage);
      } else {
        const errorMessage = response.data.message || (lang === 'ko' ? '경매 동의에 실패했습니다.' : 'Failed to agree to auction terms.');
        toast.error(errorMessage);
      }
    } catch (error: any) {
      devError('경매 동의 실패:', error);
      const errorMessage = error?.response?.data?.error?.customMessage || error?.response?.data?.message || error?.message || (lang === 'ko' ? '경매 동의에 실패했습니다.' : 'Failed to agree to auction terms.');
      toast.error(errorMessage);
    } finally {
      setIsAgreeLoading(false);
    }
  };

  const handleBidSubmit = async () => {
    if (!lotData?.index || !selectedBidPrice) {
      const errorMessage = lang === 'ko' ? '입찰 정보가 올바르지 않습니다.' : 'Bid information is invalid.';
      toast.error(errorMessage);
      return;
    }

    try {
      // 사전 입찰 API 호출
      const response = await preBid(lotData.index, {
        price: parseInt(selectedBidPrice)
      });

      if (response.data.statusCode === 200 || response.data.statusCode === 201) {
        // 성공 토스트 메시지 - 프론트에서 국제화 처리
        const successMessage = lang === 'ko' ? '입찰 신청이 완료되었습니다!' : 'Bid request has been completed!';
        toast.success(successMessage);
        
        // 성공 후 이전 페이지로 돌아가기
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      } else {
        // 에러 메시지 - 서버 응답 우선, 없으면 프론트 국제화 처리
        const errorMessage = response.data.message || (lang === 'ko' ? '입찰 신청에 실패했습니다.' : 'Bid request failed.');
        toast.error(errorMessage);
      }
    } catch (error: any) {
      devError('입찰 신청 실패:', error);
      
      // 에러 응답에서 메시지 추출 - 서버 응답 우선, 없으면 프론트 국제화 처리
      let errorMessage = lang === 'ko' ? '입찰 신청에 실패했습니다.' : 'Bid request failed.';
      
      const apiErrorMessage = error?.response?.data?.error?.customMessage || error?.response?.data?.message;
      if (apiErrorMessage) {
        // 서버에서 국제화된 에러 메시지가 있으면 사용
        errorMessage = apiErrorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleIncomplete = () => {
    // 완료되지 않았을 때의 처리
    devLog('입찰 신청이 완료되지 않았습니다');
  };

  const handleDisabled = () => {
    // 비활성화 상태일 때의 처리
    const warningMessage = lang === 'ko' ? '모든 정보를 입력하고 동의해주세요' : 'Please enter all information and agree to terms';
    toast.warning(warningMessage);
  };

  const Layout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>
            {/* 정보 섹션 */}
            <InfoSection>
              <LotImage 
                src={lotData?.images?.[0] ? getImageSrc(lotData.images[0]) : "/common/no_image.svg"} 
                alt="Lot Image"
                onError={(e) => {
                  e.currentTarget.src = '/common/no_image.svg';
                }}
              />
              <LotInfoContainer>
                <LotName>{lotData?.name || 'LOT 정보를 불러올 수 없습니다.'}</LotName>
                <LotId>{lotData?.code || `LOT.${lotData?.index || '---'}`}</LotId>
              </LotInfoContainer>
            </InfoSection>

            {/* 금액 섹션 */}
            <div>
              <FormTitle>{texts.amountSection}</FormTitle>
              <AmountSection>
                <DropdownContainer>
                  <CustomDropdown
                    value={selectedBidPrice}
                    onChange={setSelectedBidPrice}
                    options={bidPriceOptions}
                    placeholder={
                      <DropdownOptionContainer>
                        <CurrencyText>{getCurrencyLabel()}</CurrencyText>
                        <AmountText>{texts.bidAmountPlaceholder}</AmountText>
                      </DropdownOptionContainer>
                    }
                  />
                </DropdownContainer>
                {selectedBidPrice && !shouldHideByAgreement && (
                  <FeeCalculationContainer>
                    {(() => {
                      const bidPrice = parseInt(selectedBidPrice);
                      const fees = calculateFees(bidPrice);
                      return (
                        <>
                          <FeeRow>
                            <span>{texts.auctionHouseFee} ({feeRates.auctionHouseFeeRate}%)</span>
                            <span>{getCurrencyLabel()} {formatNumber(fees.auctionHouseFee)}</span>
                          </FeeRow>
                          <FeeRow>
                            <span>{texts.onlineAuctionFee} ({feeRates.onlineAuctionFeeRate}%)</span>
                            <span>{getCurrencyLabel()} {formatNumber(fees.onlineAuctionFee)}</span>
                          </FeeRow>
                          {fees.isDomestic && (
                            <>
                              <FeeRow>
                                <TooltipContainer>
                                  <span>{texts.feeVAT} ({feeRates.vatRate}%)</span>
                                  <QuestionIcon 
                                    ref={questionIconRef}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowVATTooltip(!showVATTooltip);
                                    }}
                                  >
                                    ?
                                  </QuestionIcon>
                                  <Tooltip 
                                    visible={showVATTooltip}
                                    $left={tooltipPosition.left}
                                    $transform={tooltipPosition.transform}
                                    $arrowLeft={tooltipPosition.arrowLeft}
                                  >
                                    {texts.feeVATTooltip}
                                  </Tooltip>
                                </TooltipContainer>
                                <span>{getCurrencyLabel()} {formatNumber(fees.feeVAT)}</span>
                              </FeeRow>
                              <FeeRow>
                                <TooltipContainer>
                                  <span>{texts.onlineFeeVAT} ({feeRates.vatRate}%)</span>
                                  <QuestionIcon
                                    ref={onlineQuestionIconRef}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowVATTooltip(false);
                                      setShowOnlineVATTooltip(!showOnlineVATTooltip);
                                    }}
                                  >
                                    ?
                                  </QuestionIcon>
                                  <Tooltip
                                    visible={showOnlineVATTooltip}
                                    $left={onlineTooltipPosition.left}
                                    $transform={onlineTooltipPosition.transform}
                                    $arrowLeft={onlineTooltipPosition.arrowLeft}
                                  >
                                    {texts.onlineFeeVATTooltip}
                                  </Tooltip>
                                </TooltipContainer>
                                <span>{getCurrencyLabel()} {formatNumber(fees.onlineFeeVAT)}</span>
                              </FeeRow>
                            </>
                          )}
                          <FeeDivider />
                          <TotalRow>
                            <span>{texts.totalAmount}</span>
                            <span>{getCurrencyLabel()} {formatNumber(fees.totalAmount)}</span>
                          </TotalRow>
                        </>
                      );
                    })()}
                  </FeeCalculationContainer>
                )}
              </AmountSection>
            </div>

            {/* 동의 섹션 */}
            <div>
              {/* 입찰 시 유의사항 */}
              {!shouldHideByAgreement && <BiddingNoticeContainer>
                <BiddingNoticeTitle>
                  <InfoIcon viewBox="0 0 24 24">
                    <path d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2ZM13,17H11V11H13V17ZM13,9H11V7H13V9Z" />
                  </InfoIcon>
                  {texts.biddingNoticeTitle}
                </BiddingNoticeTitle>
                <BiddingNoticeList>
                  <BiddingNoticeItem>{texts.biddingNoticeItem1}</BiddingNoticeItem>
                  <BiddingNoticeItem>{texts.biddingNoticeItem2}</BiddingNoticeItem>
                  <BiddingNoticeItem>{texts.biddingNoticeItem3}</BiddingNoticeItem>
                </BiddingNoticeList>
              </BiddingNoticeContainer>}
              
              {!shouldHideByAgreement && (
                <AgreementSectionWrapper>
                  <AgreementItem>
                    <AgreementButton
                      isClicked={isIndividualAgreed}
                      onClicked={handleAgreeAuction}
                    >
                      {texts.auctionTermsTitle}
                    </AgreementButton>
                    <ViewButton onClick={handleViewTerms}>
                      보기
                    </ViewButton>
                  </AgreementItem>
                </AgreementSectionWrapper>
              )}
            </div>
          </ContentContainer>
          
          {/* 고정 버튼 영역 */}
          <BottomFixedAction showBorderTop align="center">
            <AnimatedBorderButton
              title={texts.bidButton}
              tooltipText={texts.bidButtonTooltip}
              onPressed={handleBidSubmit}
              onIncomplete={handleIncomplete}
              disabledCallback={handleDisabled}
              duration={1}
              height={48}
              borderRadius={16}
              borderThickness={3}
              backgroundColor="#A6D4D1"
              finalBorderColor="#22c55e"
              textColor="#262626"
              textPressedColor="#FFFFFF"
              textCompletedColor="#FFFFFF"
              disabled={!isFormValid()}
              hideTooltip={showTermsSheet} // 바텀시트가 열릴 때 툴팁 숨기기
            />
          </BottomFixedAction>
        </Container>
        
        {/* 약관 동의서 시트 */}
        {showTermsSheet && (
          <TermsConsentSheet
            sections={[termsSection]}
            viewedSection={viewedSection}
            onClose={handleCloseTermsSheet}
            onSectionClick={handleSectionClick}
            buttonText={isLoadingTerms ? '로딩 중...' : texts.agreeButton}
            onButtonClick={() => {
              if (!isLoadingTerms) {
                handleAgreeAuction();
              }
            }}
          />
        )}
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={Layout}
      desktopView={Layout}
    />
  );
}

export default BidRequest;
